// components/ChoreFormDialog.tsx
import React from 'react';
import { Schedule } from '../domain';
import AssigneePicker from './AssigneePicker';
import LabelsInput from './LabelsInput';
import ScheduleField from './ScheduleField';
import {
  Alert, Button, Dialog, DialogBody, DialogFooter, DialogFooterSpacer, Field, Input, SegmentedControl, Select, Switch, Textarea,
} from './ui';

/** More households than this and the switcher becomes a select. */
const MAX_SEGMENTS = 3;

export interface ChoreFormValues {
  name: string;
  description: string;
  labels: string[];
  schedule: Schedule;
  assignments: { userId: string; isResponsible: boolean }[];
}

interface ChoreFormDialogProps {
  mode: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  values: ChoreFormValues;
  onChange: <K extends keyof ChoreFormValues>(field: K, value: ChoreFormValues[K]) => void;
  members: { id: string; name: string }[];
  toggleAssignment: (userId: string) => void;
  toggleResponsible: (userId: string) => void;
  /** Adding only: where the chore goes. An existing chore stays in its household. */
  households?: { id: string; name: string }[];
  householdId?: string;
  onHouseholdChange?: (id: string) => void;
  /** Editing: the household it's in, named in the header. */
  householdName?: string;
  /** Adding only: keep the dialog open for the next one. */
  addAnother?: boolean;
  onAddAnotherChange?: (value: boolean) => void;
  loading?: boolean;
  canSubmit?: boolean;
  error?: string;
}

/** The one form for a chore, for adding a new one and for editing one. */
const ChoreFormDialog: React.FC<ChoreFormDialogProps> = ({
  mode, open, onClose, onSubmit, values, onChange, members, toggleAssignment, toggleResponsible,
  households = [], householdId = '', onHouseholdChange, householdName, addAnother, onAddAnotherChange,
  loading, canSubmit = true, error,
}) => {
  const adding = mode === 'add';
  const id = (field: string) => `chore-form-${mode}-${field}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={adding ? 'New chore' : 'Edit chore'}
      description={adding ? 'It shows up on the dashboard when it’s due.' : householdName && `In ${householdName}`}
      size="lg"
    >
      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <DialogBody>
          {adding && households.length > 1 && (
            <Field label="Household" htmlFor={households.length > MAX_SEGMENTS ? id('household') : undefined}>
              {households.length > MAX_SEGMENTS ? (
                <Select id={id('household')} value={householdId} onChange={(e) => onHouseholdChange?.(e.target.value)} required>
                  {households.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </Select>
              ) : (
                <div>
                  <SegmentedControl
                    label="Household"
                    value={householdId}
                    onChange={(value) => onHouseholdChange?.(value)}
                    options={households.map(h => ({ value: h.id, label: h.name }))}
                  />
                </div>
              )}
            </Field>
          )}

          <Field label="Name" htmlFor={id('name')}>
            <Input
              id={id('name')}
              type="text"
              value={values.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder={adding ? 'e.g. Descale the coffee machine' : undefined}
              data-autofocus={adding || undefined}
              required
            />
          </Field>

          <Field label="Notes" htmlFor={id('description')} optional>
            <Textarea
              id={id('description')}
              rows={2}
              value={values.description}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="Links, instructions, where things are kept"
            />
          </Field>

          <Field label="Repeats">
            <ScheduleField value={values.schedule} onChange={(schedule) => onChange('schedule', schedule)} />
          </Field>

          <Field label="Labels" optional>
            <LabelsInput labels={values.labels} onChange={(labels) => onChange('labels', labels)} />
          </Field>

          <Field label="Assign to" hint={members.length > 0 ? 'The star marks who usually does it.' : undefined}>
            <AssigneePicker
              members={members}
              assignments={values.assignments}
              toggleAssignment={toggleAssignment}
              toggleResponsible={toggleResponsible}
            />
          </Field>

          {error && <Alert tone="error">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          {adding && onAddAnotherChange && (
            <>
              <Switch checked={!!addAnother} onChange={onAddAnotherChange} label="Add another" />
              <DialogFooterSpacer />
            </>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={loading} disabled={!canSubmit}>
            {adding ? 'Add chore' : 'Save changes'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
};

export default ChoreFormDialog;
